import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import JSZip from 'jszip';
import Spinner from '../Atoms/Spinner';
import Slider from '../Components/Slider';
import { createPortal } from 'react-dom';

const PageContainer = styled.div`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    position: relative;

    ${props => props.backgroundImage ? `
        background-image: url(${props.backgroundImage});
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
            z-index: 0;
        }
    ` : `
        background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    `}
`;

const ContentContainer = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
`;

const AccessCard = styled.div`
    background: white;
    padding: 60px;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.08);
    text-align: center;
    max-width: 500px;
    margin: 0 auto;

    h1 {
        font-size: 32px;
        margin-bottom: 16px;
        color: #2c3e50;
    }

    p {
        font-size: 18px;
        color: #7f8c8d;
        margin-bottom: 40px;
    }
`;

const AccessForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const AccessInput = styled.input`
    padding: 16px 20px;
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    font-size: 18px;
    transition: all 0.2s ease;
    text-align: center;
    letter-spacing: 2px;

    &:focus {
        outline: none;
        border-color: #9b59b6;
    }

    &::placeholder {
        letter-spacing: normal;
    }
`;

const AccessButton = styled.button`
    padding: 16px 32px;
    background: linear-gradient(135deg, #9b59b6, #8e44ad);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(155, 89, 182, 0.3);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ErrorMessage = styled.div`
    color: #e74c3c;
    font-size: 16px;
    margin-top: 10px;
`;

const PressContent = styled.div`
    background: white;
    padding: 80px;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.08);

    @media (max-width: 768px) {
        padding: 40px 20px;
    }
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 60px;

    h1 {
        font-size: 48px;
        margin-bottom: 16px;
        color: #2c3e50;
        
        @media (max-width: 768px) {
            font-size: 32px;
        }
    }

    .subtitle {
        font-size: 20px;
        color: #7f8c8d;
    }
`;

const DocumentsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    margin-top: 60px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const DocumentCard = styled.div`
    background: #f8f9fa;
    padding: 20px;
    border-radius: 16px;
    transition: all 0.3s ease;
    text-align: center;
    cursor: pointer;
    position: relative;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(0,0,0,0.1);
        background: #f0f0f0;
    }

    &:hover::after {
        content: '🔍 Cliquez pour agrandir';
        position: absolute;
        bottom: 30px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 14px;
        white-space: nowrap;
        pointer-events: none;
    }

    .title {
        font-size: 20px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 20px;
    }

    .slider-container {
        height: 300px;
        margin-bottom: 16px;
        pointer-events: none;
    }
`;

const LFDLogo = styled.div`
    text-align: center;
    margin-bottom: 40px;

    img {
        height: 80px;
    }
`;

const DownloadAllButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 16px 32px;
    background: linear-gradient(135deg, #27ae60, #229954);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 40px auto 0;
    display: flex;
    justify-content: center;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(39, 174, 96, 0.3);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const FullscreenModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    animation: fadeIn 0.2s ease;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 30px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h2 {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
    }
`;

const ModalActions = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const DownloadButton = styled.button`
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: white;
    font-size: 16px;
    cursor: pointer;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;
    gap: 8px;
    font-weight: 500;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.5);
    }
`;

const CloseButton = styled.button`
    background: transparent;
    border: none;
    color: white;
    font-size: 36px;
    cursor: pointer;
    padding: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const ModalContent = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    overflow: hidden;

    .slider-wrapper {
        width: 100%;
        height: 100%;
        max-width: 1400px;
        max-height: 90vh;
    }
`;

const PresseFD100 = () => {
    const { eventId } = useParams();
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [accessCode, setAccessCode] = useState('');
    const [error, setError] = useState('');
    const [eventData, setEventData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [fullscreenDocument, setFullscreenDocument] = useState(null);

    useEffect(() => {
        checkAuthentication();
    }, [eventId]);

    // Handle keyboard escape to close fullscreen
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && fullscreenDocument) {
                setFullscreenDocument(null);
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [fullscreenDocument]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (fullscreenDocument) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [fullscreenDocument]);

    const checkAuthentication = async () => {
        try {
            // Check if already authenticated for this event
            const authKey = `press_auth_${eventId}`;
            const isAuth = localStorage.getItem(authKey) === 'true';
            
            if (true) { //isAuth removed to always fetch event data
                await fetchEventData();
                setAuthenticated(true);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error checking authentication:', error);
            setLoading(false);
        }
    };

    const fetchEventData = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/events/${eventId}/press-public`
            );
            setEventData(response.data);
        } catch (error) {
            console.error('Error fetching event data:', error);
            setError('Erreur lors du chargement des données');
        }
    };

    const handleAccessSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/events/${eventId}/verify-press-code`,
                { accessCode }
            );

            if (response.data.success) {
                localStorage.setItem(`press_auth_${eventId}`, 'true');
                await fetchEventData();
                setAuthenticated(true);
            } else {
                setError('Code d\'accès incorrect');
            }
        } catch (error) {
            console.error('Error verifying access code:', error);
            setError('Code d\'accès incorrect');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadAll = async () => {
        if (!eventData || !eventData.pressPage) return;

        setDownloading(true);
        const { pressPage } = eventData;
        const zip = new JSZip();

        // Helper function to get file extension from path
        const getExtension = (path) => {
            if (!path) return '';
            const parts = path.split('.');
            return parts.length > 1 ? '.' + parts.pop() : '';
        };

        try {
            // Collect all document URLs
            const downloads = [];

            if (pressPage.keyVisualUrl) {
                const ext = getExtension(pressPage.keyVisualPath);
                downloads.push({
                    url: process.env.REACT_APP_MEDIA_BASE_URL+'/../'+pressPage.keyVisualPath,
                    name: (pressPage.keyVisualName || 'key-visual') + ext
                });
            }
            if (pressPage.pressReleaseUrl) {
                const ext = getExtension(pressPage.pressReleasePath);
                downloads.push({
                    url: process.env.REACT_APP_MEDIA_BASE_URL+'/../'+pressPage.pressReleasePath,
                    name: (pressPage.pressReleaseName || 'communique-presse') + ext
                });
            }
            if (pressPage.pressKitUrl) {
                const ext = getExtension(pressPage.pressKitPath);
                downloads.push({
                    url: process.env.REACT_APP_MEDIA_BASE_URL+'/../'+pressPage.pressKitPath,
                    name: (pressPage.pressKitName || 'dossier-presse') + ext
                });
            }
            if (pressPage.fourAnglesUrl) {
                const ext = getExtension(pressPage.fourAnglesPath);
                downloads.push({
                    url: process.env.REACT_APP_MEDIA_BASE_URL+'/../'+pressPage.fourAnglesPath,
                    name: (pressPage.fourAnglesName || '4-angles') + ext
                });
            }
            if (pressPage.customDocuments && pressPage.customDocuments.length > 0) {
                pressPage.customDocuments.forEach(doc => {
                    const ext = getExtension(doc.filePath);
                    downloads.push({
                        url: process.env.REACT_APP_MEDIA_BASE_URL+'/../'+doc.filePath,
                        name: (doc.title || 'document') + ext
                    });
                });
            }

            // Fetch and add all files to the zip
            for (const { url, name } of downloads) {
                try {
                    const response = await fetch(url);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch file: ${url}`);
                    }
                    const blob = await response.blob();
                    zip.file(name, blob);
                } catch (error) {
                    console.error(`Error fetching file ${url}:`, error);
                }
            }

            // Generate the zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Trigger the download
            const link = document.createElement('a');
            link.href = URL.createObjectURL(zipBlob);
            link.download = `${eventData.title_fr || 'documents-presse'}.zip`;
            link.click();

            // Clean up the object URL
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Error downloading files:', error);
            alert('Erreur lors du téléchargement des fichiers');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <Spinner />
            </PageContainer>
        );
    }

    if (!authenticated) {
        return (
            <PageContainer>
                <AccessCard>
                    <LFDLogo>
                        <img src="/logo-lfd.png" alt="Le French Design" />
                    </LFDLogo>
                    <h1>Espace Presse</h1>
                    <p>Veuillez entrer le code d'accès pour accéder aux ressources presse</p>
                    
                    <AccessForm onSubmit={handleAccessSubmit}>
                        <AccessInput
                            type="password"
                            placeholder="Code d'accès"
                            value={accessCode}
                            onChange={(e) => setAccessCode(e.target.value)}
                            required
                        />
                        <AccessButton type="submit" disabled={submitting}>
                            {submitting ? 'Vérification...' : 'Accéder'}
                        </AccessButton>
                    </AccessForm>
                    
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </AccessCard>
            </PageContainer>
        );
    }

    if (!eventData || !eventData.pressPage) {
        return (
            <PageContainer>
                <AccessCard>
                    <h1>Page non disponible</h1>
                    <p>Les ressources presse pour cet événement ne sont pas encore disponibles.</p>
                </AccessCard>
            </PageContainer>
        );
    }

    const { pressPage } = eventData;

    // Get background image URL if available
    // Use MEDIA_BASE_URL (without /api) for static file access
    const backgroundImageUrl = pressPage.backgroundImagePath
        ? `${process.env.REACT_APP_MEDIA_BASE_URL}${pressPage.backgroundImagePath}`
        : null;

    console.log('Press page data:', pressPage);
    console.log('Background image URL constructed:', backgroundImageUrl);

    // Build slides array for the slider
    const buildSlides = () => {
        const slides = [];
        
        if (pressPage.keyVisualUrl) {
            slides.push({
                path: `../${pressPage.keyVisualPath}`,
                type: 'image',
                legende: 'Key Visual'
            });
        }
        
        if (pressPage.pressReleaseUrl) {
            slides.push({
                path: `../${pressPage.pressReleasePath}`,
                type: 'pdf',
                legende: 'Communiqué de presse'
            });
        }
        
        if (pressPage.pressKitUrl) {
            slides.push({
                path: `../${pressPage.pressKitPath}`,
                type: 'pdf',
                legende: 'Dossier de presse'
            });
        }
        
        if (pressPage.fourAnglesUrl) {
            slides.push({
                path: `../${pressPage.fourAnglesPath}`,
                type: 'pdf',
                legende: 'Document "4 angles"'
            });
        }

        // Add custom documents
        if (pressPage.customDocuments && pressPage.customDocuments.length > 0) {
            pressPage.customDocuments.forEach(doc => {
                slides.push({
                    path: `../${doc.filePath}`,
                    type: doc.fileType,
                    legende: doc.title
                });
            });
        }

        return slides;
    };

    const slides = buildSlides();

    const openFullscreen = (title, slide, filePath) => {
        setFullscreenDocument({ title, slide, filePath });
    };

    const handleDownloadDocument = async (filePath, title) => {
        try {
            // Helper function to get file extension from path
            const getExtension = (path) => {
                if (!path) return '';
                const parts = path.split('.');
                return parts.length > 1 ? '.' + parts.pop() : '';
            };

            const ext = getExtension(filePath);
            const url = process.env.REACT_APP_MEDIA_BASE_URL + '/../' + filePath;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to fetch file: ${url}`);
            }
            const blob = await response.blob();

            // Create download link
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = title + ext;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Error downloading document:', error);
            alert('Erreur lors du téléchargement du document');
        }
    };

    return (
        <>
            <PageContainer backgroundImage={backgroundImageUrl}>
                <ContentContainer>
                    <PressContent>
                        <Header>
                            <h1>{eventData.title_fr}</h1>
                            <div className="subtitle">Ressources Presse</div>
                        </Header>

                        {slides.length > 0 && (
                            <DocumentsGrid>
                                {pressPage.keyVisualUrl && (
                                    <DocumentCard onClick={() => openFullscreen('Key Visual', {
                                        path: `../${pressPage.keyVisualPath}`,
                                        type: 'image',
                                        legende: 'Key Visual'
                                    }, pressPage.keyVisualPath)}>
                                        <div className="title">Key Visual</div>
                                        <div className="slider-container">
                                            <Slider
                                                slides={[{
                                                    path: `../${pressPage.keyVisualPath}`,
                                                    type: 'image',
                                                    legende: 'Key Visual'
                                                }]}
                                                height="100%"
                                                hideDocumentLink={true}
                                                hideLegend={true}
                                            />
                                        </div>
                                    </DocumentCard>
                                )}

                                {pressPage.pressReleaseUrl && (
                                    <DocumentCard onClick={() => openFullscreen('Communiqué de presse', {
                                        path: `../${pressPage.pressReleasePath}`,
                                        type: 'pdf',
                                        legende: 'Communiqué de presse'
                                    }, pressPage.pressReleasePath)}>
                                        <div className="title">Communiqué de presse</div>
                                        <div className="slider-container">
                                            <Slider
                                                slides={[{
                                                    path: `../${pressPage.pressReleasePath}`,
                                                    type: 'pdf',
                                                    legende: 'Communiqué de presse'
                                                }]}
                                                height="100%"
                                                hideDocumentLink={true}
                                                hideLegend={true}
                                            />
                                        </div>
                                    </DocumentCard>
                                )}

                                {pressPage.pressKitUrl && (
                                    <DocumentCard onClick={() => openFullscreen('Dossier de presse', {
                                        path: `../${pressPage.pressKitPath}`,
                                        type: 'pdf',
                                        legende: 'Dossier de presse'
                                    }, pressPage.pressKitPath)}>
                                        <div className="title">Dossier de presse</div>
                                        <div className="slider-container">
                                            <Slider
                                                slides={[{
                                                    path: `../${pressPage.pressKitPath}`,
                                                    type: 'pdf',
                                                    legende: 'Dossier de presse'
                                                }]}
                                                height="100%"
                                                hideDocumentLink={true}
                                                hideLegend={true}
                                            />
                                        </div>
                                    </DocumentCard>
                                )}

                                {pressPage.fourAnglesUrl && (
                                    <DocumentCard onClick={() => openFullscreen('4 angles', {
                                        path: `../${pressPage.fourAnglesPath}`,
                                        type: 'pdf',
                                        legende: 'Document "4 angles"'
                                    }, pressPage.fourAnglesPath)}>
                                        <div className="title">4 angles</div>
                                        <div className="slider-container">
                                            <Slider
                                                slides={[{
                                                    path: `../${pressPage.fourAnglesPath}`,
                                                    type: 'pdf',
                                                    legende: 'Document "4 angles"'
                                                }]}
                                                height="100%"
                                                hideDocumentLink={true}
                                                hideLegend={true}
                                            />
                                        </div>
                                    </DocumentCard>
                                )}

                                {pressPage.customDocuments && pressPage.customDocuments.length > 0 &&
                                    pressPage.customDocuments.map((doc) => (
                                        <DocumentCard key={doc._id} onClick={() => openFullscreen(doc.title, {
                                            path: `../${doc.filePath}`,
                                            type: doc.fileType,
                                            legende: doc.title
                                        }, doc.filePath)}>
                                            <div className="title">{doc.title}</div>
                                            <div className="slider-container">
                                                <Slider
                                                    slides={[{
                                                        path: `../${doc.filePath}`,
                                                        type: doc.fileType,
                                                        legende: doc.title
                                                    }]}
                                                    height="100%"
                                                    hideDocumentLink={true}
                                                    hideLegend={true}
                                                />
                                            </div>
                                        </DocumentCard>
                                    ))
                                }
                            </DocumentsGrid>
                        )}

                        {slides.length > 0 && (
                            <DownloadAllButton onClick={handleDownloadAll} disabled={downloading}>
                                {downloading ? '⏳ Téléchargement en cours...' : '📥 Télécharger tous les documents'}
                            </DownloadAllButton>
                        )}
                    </PressContent>
                </ContentContainer>
            </PageContainer>

            {fullscreenDocument && createPortal(
                <FullscreenModal onClick={() => setFullscreenDocument(null)}>
                    <ModalHeader onClick={(e) => e.stopPropagation()}>
                        <h2>{fullscreenDocument.title}</h2>
                        <ModalActions>
                            <DownloadButton
                                onClick={() => handleDownloadDocument(fullscreenDocument.filePath, fullscreenDocument.title)}
                                aria-label="Télécharger"
                            >
                                📥 Télécharger
                            </DownloadButton>
                            <CloseButton onClick={() => setFullscreenDocument(null)} aria-label="Fermer">
                                ×
                            </CloseButton>
                        </ModalActions>
                    </ModalHeader>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <div className="slider-wrapper">
                            <Slider
                                slides={[fullscreenDocument.slide]}
                                height="100%"
                                hideDocumentLink={true}
                                hideLegend={true}
                            />
                        </div>
                    </ModalContent>
                </FullscreenModal>,
                document.body
            )}
        </>
    );
};

export default PresseFD100;